import { service } from '@ember/service'
import Component from '@glimmer/component'
import { colors } from '@colors'
import { tracked } from '@glimmer/tracking'
import { t, type IntlService } from 'ember-intl'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import ChartInfoTooltip from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import SortColumnIcon from 'teacher-ui-client/components/sort-column-icon'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { eq } from 'ember-truth-helpers'
import { htmlSafe, isHTMLSafe, type SafeString } from '@ember/template'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import { type LegendsItem, Legend, TH, TD } from 'teacher-ui-client/components/giraffe/primitives/history-tables'
import type Student from 'teacher-ui-client/models/student'
import BaseHighcharterComponent from 'teacher-ui-client/components/base/highcharter'
import { differenceWith } from 'lodash'
import type RouterService from '@ember/routing/router-service'
import type Owner from '@ember/owner'
import type { AwardsEarnedHistoryData } from 'teacher-ui-client/utils/student-events/fetchers/awards-earned-history'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface HistoryDataViewModel {
  studentId: string
  firstName: string
  lastName: string
  teacherName: string
  gradePosition: number
  gold: Nullable<number>
  silver: Nullable<number>
  bronze: Nullable<number>
  totalCertificates: number
}

interface Args {
  certificatesHistoryData: AwardsEarnedHistoryData[]
  product: 're' | 'maths'
  studentRouteName: string
  subco?: boolean
  tooltip: string | SafeString
  uiScope: UIScopeName
  students: Student[]
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

type CategoryKey = 'bronze' | 'silver' | 'gold'
const chartCategories: CategoryKey[] = ['gold', 'silver', 'bronze']
const legendCategories: CategoryKey[] = ['bronze', 'silver', 'gold']

interface ChartArgs {
  data: HistoryDataViewModel
  showYAxisLabels: boolean
  maxValue: number
}

const buildChartOptions = function (chartOptions: ChartArgs): Highcharts.Options {
  const yAxis: Highcharts.YAxisOptions = {
    title: {
      text: '',
    },
    opposite: true,
    max: chartOptions.maxValue,
    minorTicks: false,
    labels: {
      enabled: chartOptions.showYAxisLabels,
    },
    offset: -10,
  }

  const series = chartCategories.map((category) => {
    return {
      type: 'bar' as const,
      name: category,
      data: [chartOptions.data[category] ?? 0],
    }
  })

  return {
    series,
    chart: {
      type: 'bar',
      // Make some room for the yaxis labels
      margin: chartOptions.showYAxisLabels ? [20, 0, 0, 0] : [0, 0, 0, 0],
      plotBackgroundColor: colors.dustyBlack50,
      // always disable animation for this chart, as they're all separate charts and don't animate together
      animation: false,
    },
    tooltip: {
      enabled: true,
      formatter: function (this: Highcharts.Point) {
        return `<b>${this.y}</b>`
      },
      backgroundColor: colors.dustyBlack500,
      borderColor: colors.dustyBlack500,
      style: {
        color: colors.white,
      },
    },
    colors: [colors.medalGold, colors.medalSilver, colors.medalBronze],
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    xAxis: {
      maxPadding: 0,
      minPadding: 0,
      labels: {
        enabled: false,
      },
      lineWidth: 0,
      tickWidth: 0,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    legend: { enabled: false },
    yAxis,
    plotOptions: {
      bar: {
        animation: false,
        borderWidth: 0,
        colorByPoint: false,
        stacking: 'normal',
        pointWidth: 40,
      },
    },
  }
}

export class AwardsEarnedCertificatesHistory extends Component<Signature> {
  @service intl!: IntlService

  @service router!: RouterService

  @tracked sortingPriorityQueue = []

  processor: DataProcessor<HistoryDataViewModel>

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  get data() {
    return this.tableRows
  }

  get tableRows() {
    const stats = this.args.certificatesHistoryData

    return this.args.students.filter(hasId).map((student) => {
      const statRow = stats.find((stat) => String(stat.student_id) === student.id)

      const bronze = statRow?.bronze ?? null
      const silver = statRow?.silver ?? null
      const gold = statRow?.gold ?? null
      const totalCertificates = (bronze ?? 0) + (silver ?? 0) + (gold ?? 0)

      return {
        firstName: student.firstName,
        lastName: student.lastName,
        teacherName:
          this.args.product === 'maths' ? student.mathsTeacherNames.join(', ') : student.readingTeacherNames.join(', '),
        gradePosition: student.gradePosition,
        studentId: student.id,
        bronze,
        silver,
        gold,
        totalCertificates,
      }
    })
  }

  get maxCertificates() {
    const certificatesData = this.args.certificatesHistoryData.map(
      (student) => student.gold + student.silver + student.bronze,
    )

    const highestCertificatesEarned = certificatesData ? Math.max(...certificatesData) : 10
    return Math.ceil(highestCertificatesEarned / 2) * 2 + 2 // extra space on the chart (nearest multiple of 5)
  }

  get legendCategories(): LegendsItem[] {
    const { intl } = this

    return legendCategories.map((category) => {
      switch (`${this.args.product}.${category}`) {
        case 're.bronze':
          return {
            color: 'bg-medal-bronze',
            label: intl.t('reporting.charts.awardsEarned.certificates.byMonth.bronzeLabel.re'),
          }
        case 'maths.bronze':
          return {
            color: 'bg-medal-bronze',
            label: intl.t('reporting.charts.awardsEarned.certificates.byMonth.bronzeLabel.maths'),
          }
        case 're.silver':
          return {
            color: 'bg-medal-silver',
            label: intl.t('reporting.charts.awardsEarned.certificates.byMonth.silverLabel.re'),
          }
        case 'maths.silver':
          return {
            color: 'bg-medal-silver',
            label: intl.t('reporting.charts.awardsEarned.certificates.byMonth.silverLabel.maths'),
          }
        case 're.gold':
          return {
            color: 'bg-medal-gold',
            label: intl.t('reporting.charts.awardsEarned.certificates.byMonth.goldLabel.re'),
          }
        case 'maths.gold':
          return {
            color: 'bg-medal-gold',
            label: intl.t('reporting.charts.awardsEarned.certificates.byMonth.goldLabel.maths'),
          }
        default:
          return { color: '', label: '' }
      }
    })
  }

  get rowsWithChartOptions() {
    return this.processor.transformedData.map((row, index) => {
      return {
        ...row,
        chartOptions: buildChartOptions({
          data: row,
          showYAxisLabels: index === 0,
          maxValue: this.maxCertificates,
        }),
      }
    })
  }

  get amendedTooltip() {
    const { tooltip } = this.args
    const missingStudentsCount = differenceWith(
      this.args.students,
      this.args.certificatesHistoryData ?? [],
      (student, historyStudent) => student.id === String(historyStudent.student_id),
    ).length

    if (missingStudentsCount) {
      const wasSafe = isHTMLSafe(tooltip)
      const newTooltip = `${tooltip.toString()}${this.intl.t('reporting.missingStudentsCount', {
        missingStudentsCount,
      })}`
      return wasSafe ? htmlSafe(newTooltip) : newTooltip
    }
    return tooltip
  }

  get chartTitle() {
    switch (this.args.product) {
      case 're':
        return this.intl.t('reporting.chartTitles.awardsEarnedHistory.re')
      case 'maths':
        return this.intl.t('reporting.chartTitles.awardsEarnedHistory.maths')
    }
  }

  get showChart() {
    return this.args.uiScope !== 'school'
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo(this.args.studentRouteName, studentId)
  }

  <template>
    <div
      data-test-giraffe-certificates-earned-history
      class="border-dusty-black-50 shadow-dusty-black-100 relative border shadow-md"
      ...attributes
    >
      <ChartInfoTooltip data-test-certificates-earned-tooltip @tooltipContent={{this.amendedTooltip}} />
      <div class="overflow-x-auto rounded-sm">
        <table data-test-history-table-certificates-earned class="w-full min-w-6xl text-sm">
          <thead>
            <tr
              class="{{if (eq @product 're') 'bg-oceany-blue-300'}}
                {{if (eq @product 'maths') 'bg-ms-green-300'}}
                divide-x divide-white font-semibold text-white"
            >
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                  <SortColumnIcon class @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="firstName" />
                </div>
              </TH>
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.lastName" htmlSafe=true}}

                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="lastName" />
                </div>
              </TH>
              {{#if @subco}}
                <TH
                  class="w-48"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "reporting.dashboardTables.teacher"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="teacherName" />
                  </div>
                </TH>
                <TH
                  class="w-24"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByNumber "gradePosition")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "grade.label"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="gradePosition" />
                  </div>
                </TH>
              {{/if}}
              <TH class="w-24" role="button" {{on "click" (fn this.processor.tableActions.sortByNumber "bronze")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.bronze"}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="bronze" />
                </div>
              </TH>
              <TH class="w-24" role="button" {{on "click" (fn this.processor.tableActions.sortByNumber "silver")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.silver"}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="silver" />
                </div>
              </TH>
              <TH class="w-24" role="button" {{on "click" (fn this.processor.tableActions.sortByNumber "gold")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.gold"}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="gold" />
                </div>
              </TH>
              <TH
                class="w-24"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "totalCertificates")}}
              >
                <div class="flex items-center justify-between font-bold">
                  {{t "reporting.dashboardTables.total"}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="totalCertificates" />
                </div>
              </TH>
              {{#if this.showChart}}
                <TH
                  class="{{if @subco 'w-1/3' 'w-1/2'}}
                    border-l-dusty-black-100 bg-white px-0! whitespace-nowrap text-black print:hidden"
                >
                  <Legend
                    data-test-certificates-earned-legend
                    @title={{this.chartTitle}}
                    @categories={{this.legendCategories}}
                  />
                </TH>
              {{/if}}
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 divide-y-2 print:divide-black">
            {{#each this.rowsWithChartOptions as |row index|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr
                data-test-awards-earned-certificates-history-row={{index}}
                class="cursor-pointer"
                {{on "click" (fn this.transitionToStudentRoute row.studentId)}}
              >
                <TD class="text-oceany-blue-300">
                  {{row.firstName}}
                </TD>
                <TD class="text-oceany-blue-300">
                  {{row.lastName}}
                </TD>
                {{#if @subco}}
                  <TD>
                    <div class="{{if this.showChart 'max-h-20 overflow-auto'}}">
                      {{row.teacherName}}
                    </div>
                  </TD>
                  <TD class="text-center">
                    {{gradeName row.gradePosition}}
                  </TD>
                {{/if}}
                <TD class="text-center">
                  {{formatValue row.bronze}}
                </TD>
                <TD class="text-center">
                  {{formatValue row.silver}}
                </TD>
                <TD class="text-center">
                  {{formatValue row.gold}}
                </TD>
                <TD data-test-awards-earned-certificates-history-row-total class="text-center">
                  {{formatValue row.totalCertificates}}
                </TD>
                {{#if this.showChart}}
                  <TD class="border-dusty-black-100 cursor-default border-l px-0! print:hidden">
                    <BaseHighcharterComponent
                      data-test-certificates-earned-highchart-row
                      class="h-full"
                      @options={{row.chartOptions}}
                    />
                  </TD>
                {{/if}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
  </template>
}

export default AwardsEarnedCertificatesHistory
